import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { useList } from "@refinedev/core";
import type { AuthUser } from "@/hooks/useAuth";
import { DataCard } from "@/components/forms/DataCard";
import { MoneyDisplay } from "@/components/forms/MoneyDisplay";
import { formatDate } from "@/lib/formatters";

interface Props {
  user: AuthUser;
}

export function DashboardOperador({ user }: Props) {
  const hoy = new Date().toISOString().slice(0, 10);

  const { data: ventasHoy } = useList({
    resource: "ventas",
    filters: [
      { field: "fecha_venta", operator: "eq", value: hoy },
      { field: "estado", operator: "eq", value: "confirmada" },
    ],
    pagination: { mode: "off" },
  });

  const { data: corteAbierto } = useList({
    resource: "periodos_cobro",
    filters: user.tienda_id
      ? [
          { field: "tienda_id", operator: "eq", value: user.tienda_id },
          { field: "estado", operator: "eq", value: "abierto" },
        ]
      : [],
    pagination: { mode: "off" },
    queryOptions: { enabled: !!user.tienda_id },
  });

  const { data: porPagar } = useList({
    resource: "periodos_cobro",
    filters: user.tienda_id
      ? [
          { field: "tienda_id", operator: "eq", value: user.tienda_id },
          { field: "estado", operator: "eq", value: "liquidado" },
        ]
      : [],
    pagination: { mode: "off" },
    queryOptions: { enabled: !!user.tienda_id },
  });

  const { data: ultimas } = useList({
    resource: "ventas",
    sorters: [{ field: "registrado_en", order: "desc" }],
    pagination: { pageSize: 5 },
  });

  const totalHoy = ventasHoy?.data.reduce((s, v) => s + (v.valor_total as number), 0) ?? 0;
  const corte = corteAbierto?.data[0];
  const totalPorPagar = porPagar?.data.reduce(
    (s, p) => s + (p.valor_final_transferencia as number),
    0,
  ) ?? 0;

  const hora = new Date().getHours();
  const saludo = hora < 12 ? "Buenos días" : hora < 18 ? "Buenas tardes" : "Buenas noches";

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-2xl font-bold">
          {saludo}, {user.nombre_completo.split(" ")[0]}
        </h1>
        {user.tienda_nombre && <p className="text-fg-muted">{user.tienda_nombre}</p>}
      </header>

      <div className="grid gap-3 sm:grid-cols-3">
        <DataCard title="Ventas hoy" subtitle={`${ventasHoy?.data.length ?? 0} ventas`}>
          <MoneyDisplay value={totalHoy} className="text-2xl font-bold" />
        </DataCard>

        <DataCard
          title="Tu corte abierto"
          subtitle={
            corte ? `${corte.cantidad_ventas} ventas` : "Sin corte"
          }
          footer={
            corte && (
              <Link to={`/cuentas-cobro/${corte.id}`} className="text-sm text-brand hover:underline">
                Ver detalle →
              </Link>
            )
          }
        >
          <MoneyDisplay value={(corte?.total_ventas as number) ?? 0} className="text-2xl font-bold" />
        </DataCard>

        <DataCard title="Plata que te debemos" subtitle={`${porPagar?.data.length ?? 0} cortes liquidados`}>
          <MoneyDisplay value={totalPorPagar} className="text-2xl font-bold text-success" />
        </DataCard>
      </div>

      <section>
        <h2 className="font-semibold mb-2">Últimas ventas</h2>
        {ultimas?.data.length === 0 ? (
          <div className="card p-8 text-center text-fg-muted">
            Aún no has registrado ventas. Toca el botón verde abajo.
          </div>
        ) : (
          <ul className="space-y-2">
            {ultimas?.data.map((v) => (
              <li key={v.id as string}>
                <Link to={`/ventas/${v.id}`} className="card p-3 flex items-center gap-3 hover:bg-bg">
                  <div className="flex-1">
                    <p className="font-medium text-sm">
                      #{v.numero_consecutivo as number} · {v.cliente_nombre as string}
                    </p>
                    <p className="text-xs text-fg-muted">
                      {v.producto_marca as string} {v.producto_modelo as string} ·{" "}
                      {formatDate(v.fecha_venta as string)}
                    </p>
                  </div>
                  <MoneyDisplay value={v.valor_total as number} className="font-medium text-sm" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* FAB nueva venta */}
      <Link
        to="/ventas/nueva"
        className="fixed bottom-20 md:bottom-6 right-4 md:right-6 size-14 rounded-full bg-brand text-white shadow-lg grid place-items-center hover:bg-brand-dark active:scale-95 transition-all z-30"
        aria-label="Nueva venta"
      >
        <Plus className="size-6" />
      </Link>
    </div>
  );
}
